// "Refresh tasks now" (S128): the owner polls the group's source at once instead of waiting for
// its next poll. The answer is the group with the outcome recorded, so a failing source shows
// in the panel's error and a toast; new tasks reach the table through the refreshed queries.
// A paused group keeps the button, disabled, with the reason in its tooltip.
import { ArrowsClockwise } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Tooltip } from "rizzui";

import { useT } from "../../i18n";
import { toast } from "../../layout/toast";
import { cn } from "../../ui/cn";
import { pollTaskGroup, taskGroupQuery } from "../api";
import type { TaskGroup } from "../model";
import { TOOL_CLASS, ToolTip } from "./ToolLink";

export function RefreshNowButton({ group }: { group: TaskGroup }) {
  const t = useT().tasks.group.toolbar;
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const paused = group.status !== "active";

  async function refresh() {
    setBusy(true);
    try {
      const polled = await pollTaskGroup(group.id);
      queryClient.setQueryData(taskGroupQuery(group.id).queryKey, polled);
      await queryClient.invalidateQueries({ queryKey: ["task-groups"] });
      if (polled.last_poll_error) toast(t.refreshFailed, "warn");
      else toast(t.refreshed);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "warn");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Tooltip content={<ToolTip label={t.refresh} hint={paused ? t.refreshPaused : t.refreshHint} />} placement="bottom">
      {/* A disabled button gets no pointer events, so the tooltip hangs on this wrapper. */}
      <span className="inline-flex">
        <button
          type="button"
          aria-label={t.refresh}
          aria-busy={busy}
          disabled={paused || busy}
          onClick={() => void refresh()}
          className={TOOL_CLASS}
        >
          <ArrowsClockwise weight="bold" className={cn("h-5 w-5", busy && "animate-spin")} aria-hidden />
        </button>
      </span>
    </Tooltip>
  );
}
